import { Component, ElementRef, HostListener, Inject, Input, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import * as am4core from '@amcharts/amcharts4/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { cloneDeep } from 'lodash';

import { SearchCriteriaSortFieldEnum } from '../../../../../api/mentions/enum/search-criteria-sort-field.enum';
import { SortOrderEnum } from '../../../../../api/shared/enums/sort-order.enum';
import { MediaTypeEnum, getSocialMediaTypes } from '../../../../../Common/Basic/enums/media-type.enum';

import { ChartModel } from '../../../../../Common/Charts/models/chart.model';
import { CardChartComponentHighlightColorEnum, CardChartComponentLayoutEnum } from '../../../../../Common/Charts/components/CardChart/card-chart.component';
import { Mention } from '../../../../../Common/Mentions/models/mention';
import { MultiSeriesPieChartModel } from '../../../../../Common/Charts/models/pieChart/multiSeriesPieChart/multi-series-pie-chart.model';
import { DataPointModel } from '../../../../../Common/Charts/models/data-point.model';
import { ChartMetadataModel } from '../../../../../Common/Charts/models/chart-metadata.model';

import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';

import { MentionSearchModel } from '../../../../../Monitor/Shared/models/mention-search.model';

import { NetworkTypeEnum } from '../../../../Core/enums/network-type.enum';
import { EngagementTypeEnum } from '../../../../Core/enums/engagement-type.enum';

import { SocialChartDetailsDialogService } from '../../../services/social-chart-details-dialog.service';
import { SocialSeriesService } from '../../../services/social-series.service';
import { SocialSeriesNeoFacetKeysEnum } from '../../../enums/social-series-neo-facet-keys.enum';
import { ReportSeriesFacetGeneratorEnum } from '../../../../../Common/Reports/models/report-series-facet-generator.enum';
import { ThemeAnalyticsChartTypeEnum } from '../../../enums/theme-analytics-chart-type.enum';
import { networkTypeToMediaTypeMap, networkTypeToMediaTypeModelMap } from '../../../models/network-type-to-media-type-model.map';
import { SocialSeriesMetricsEnum } from '../../../enums/social-series-metrics.enum';
import { engagementsNeoFacetRequest, mediaTypeNeoFacetRequest, mentionsByEngagementNeoFacetRequest } from '../../../models/neo-facet-requests';

import { ThemeAnalyticsChartSeriesService } from '../theme-analytics-chart-series.service';
import { ThemeAnalyticsChartOptionsService } from '../theme-analytics-chart-options.service';
import { IconBarChartModel } from '../../../../../Common/Charts/models/barChart/icon-bar-chart.model';
import { CategoryValueXYChartOptions } from '../../../../../Common/Charts/models/barChart/category-value-x-y-chart-options.model';
import { MentionSearchCriteriaModel } from '../../../../../Monitor/Shared/models/mention-search-criteria.model';
import { MediaTypeModel } from '../../../../../Monitor/Shared/models/media-type.model';
import { ChartStylesModel } from '../../../../../Common/Charts/models/chart-styles.model';
import { SocialChartTooltipService } from '../../../../Shared/services/social-chart-tooltip.service';
import { ChartGroupInputModel } from '../../../../../Common/Charts/models/chart-group-input.model';
import { TopTermsDataMappers } from '@Social/Shared/components/TopTerms/top-terms-data-mappers';
import { BucketResponseModel } from '../../../../../api/mentions/model/bucket-response.model';
import { SocialRoutingService } from '../../../../Core/services/social-routing.service';
import { SocialChartExportModel, SocialChartExportService } from '../../../services/social-chart-export.service';
import { ChartExportEventModel } from '../../../../../Common/Charts/models/chart-export-event.model';
import { userAppPreferencesDefault } from '../../../../../Core/Users/data/user-app-preferences-default';
import { ReportSeriesModel } from '@Common/Reports/models/report-series.model';

@Component({
    selector: 'agpr-theme-engagement-analytics',
    templateUrl: './theme-engagement-analytics.component.html',
    styleUrls: ['./theme-engagement-analytics.component.scss'],
    standalone: false
})
export class ThemeEngagementAnalyticsComponent implements OnInit, OnDestroy {

    @Input() id: string;

    @ViewChild('engagementProfileChartLegend', {read: ElementRef, static: true}) engagementProfileChartLegend: ElementRef;

    idToChartModelMap: Map<string, ChartModel | ChartGroupInputModel>;

    // Card charts at the top of the page
    public totalPostsSeries: ReportSeriesModel;
    public previousPeriodTotalPostsSeries: ReportSeriesModel;

    public totalEngagementsSeries: ReportSeriesModel;
    public previousPeriodTotalEngagementsSeries: ReportSeriesModel;

    public engagementsPerPostsSeries: ReportSeriesModel;
    public previousPeriodEngagementsPerPostsSeries: ReportSeriesModel;

    // Engagement
    private totalEngagementsSeriesModel: ReportSeriesModel;
    public totalEngagementsChartModel: IconBarChartModel;
    private totalEngagementsChartOptionsModel: CategoryValueXYChartOptions;

    // Engagement Rate
    private engagementRateSeriesModel: ReportSeriesModel;
    public engagementRateChartModel: IconBarChartModel;
    private engagementRateChartOptionsModel: CategoryValueXYChartOptions;

    // Engagement Type By Social Network Comparison
    public engagementTypeBySocialNetworkComparisonChartModel: ChartGroupInputModel;
    private applauseSeriesModel: ReportSeriesModel;
    private conversationSeriesModel: ReportSeriesModel;
    private amplificationSeriesModel: ReportSeriesModel;

    // card charts per social type
    public postsPerTypeSeries: ReportSeriesModel;
    public previousPeriodPostsPerTypeSeries: ReportSeriesModel;

    public engagementsPerTypeSeries: ReportSeriesModel;
    public previousPeriodTotalEngagementsPerTypeSeries: ReportSeriesModel;

    public engagementRatesPerTypeSeries: ReportSeriesModel;
    public previousPeriodEngagementsRatesPerTypeSeries: ReportSeriesModel;

    // pie chart
    public engagementTypesChart: MultiSeriesPieChartModel;
    private engagementTypesSeries: ReportSeriesModel;
    private previousPeriodEngagementTypesSeries: ReportSeriesModel;

    // horizontal charts
    public totalLikesSeries: ReportSeriesModel;
    public previousPeriodTotalLikesSeries: ReportSeriesModel;

    public totalQuotesSeries: ReportSeriesModel;
    public previousPeriodTotalQuotesSeries: ReportSeriesModel;

    public totalRepliesSeries: ReportSeriesModel;
    public previousPeriodTotalRepliesSeries: ReportSeriesModel;

    public totalRetweetsSeries: ReportSeriesModel;
    public previousPeriodTotalRetweetsSeries: ReportSeriesModel;

    public totalViewsSeries: ReportSeriesModel;
    public previousPeriodTotalViewsSeries: ReportSeriesModel;

    private engagementSummaryExportData: {[key: string]: any} = {
        label: null,
        posts: null,
        previousPeriodPosts: null,
        engagements: null,
        previousPeriodEngagements: null,
        engagementRate: null,
        previousPeriodEngagementRate: null,
        likes: null,
        shares: null,
        replies: null,
        quotes: null,
        views: null
    };

    // tops posts
    public topPostsSeries: ReportSeriesModel;
    public topPostsSortField: SearchCriteriaSortFieldEnum;

    // tops terms
    public topTermsSeries: ReportSeriesModel;
    public topTermsSortField: SearchCriteriaSortFieldEnum;

    subscriptions: Subscription[] = [];
    showSentimentRiverChart: boolean = false;
    isArchiveSearch: boolean = false;
    hideSeries: ReportSeriesModel[];

    public readonly allSocialTypes = NetworkTypeEnum;

    public currentSocialType: NetworkTypeEnum = NetworkTypeEnum.twitter;

    public enabledNetworkTypes: NetworkTypeEnum[] = [
        NetworkTypeEnum.all,
        NetworkTypeEnum.twitter,
        NetworkTypeEnum.facebook,
        NetworkTypeEnum.instagram,
        NetworkTypeEnum.youtube,
        NetworkTypeEnum.reddit,
        NetworkTypeEnum.linkedin
    ];
    mockMetadata: ChartMetadataModel;

    private socialMediaToDisplayMap: Map<MediaTypeEnum, string>;

    readonly cardChartComponentHighlightColorEnum = CardChartComponentHighlightColorEnum;
    readonly cardChartComponentLayoutEnum = CardChartComponentLayoutEnum;
    readonly supportedExportFormats = [ 'png', 'jpg', 'svg', 'json', 'csv', 'print' ];
    readonly themeAnalyticsChartTypeEnum = ThemeAnalyticsChartTypeEnum;
    readonly socialMetrics = SocialSeriesMetricsEnum;
    readonly topTermsConfig = {
        mainTitle: {
            mode: 'multi',
            title: '',
        },
        subTitle: {
            mode: 'dynamic',
            title: 'Social.intelligence.charts.topTerms.heading.mostUsed'
        }
    };
    readonly defaultChartColors = userAppPreferencesDefault.reportsPrefs.defaultChartColors;

    constructor(
        public localizationService: LocalizationService,
        public themeAnalyticsChartSeriesService: ThemeAnalyticsChartSeriesService,
        public themeAnalyticsChartOptionsService: ThemeAnalyticsChartOptionsService,
        public socialChartDetailsDialogService: SocialChartDetailsDialogService,
        public socialSeriesService: SocialSeriesService,
        protected socialChartTooltipService: SocialChartTooltipService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        private socialRoutingService: SocialRoutingService,
        public chartExportService: SocialChartExportService,
        public el: ElementRef
    ) {
        const noContentLabelText = this.localizationService.transform('Social.intelligence.wordCloudChart.noContent');
        this.mockMetadata = { noContentLabel: `<span style="font-size: 0.875rem;">${noContentLabelText}</span>`} as ChartMetadataModel;
    }

    @HostListener('click', ['$event.target'])
    onClick(event){
        if (event?.classList.contains('viewMentionLink') && event?.classList.length > 1) {
            const chartClass = event.classList[1];
            const chartModel = this.idToChartModelMap.get(chartClass);

            this.socialChartDetailsDialogService.openDrillDownPage(
                event.href,
                chartModel?.clone ? chartModel?.clone() : chartModel
            );
            return false;
        }
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.socialSeriesService.availableNetworks$.subscribe(availableNetworks => this.enabledNetworkTypes = availableNetworks),
            this.socialRoutingService.isArchiveSearch$.subscribe(isArchiveSearch => {
                this.isArchiveSearch = isArchiveSearch;
                this.initializeAll();
                this.idToChartModelMap = new Map<string, ChartModel | ChartGroupInputModel>([
                    // the string here should match the id in the tooltip link id in tooltipHTML
                    ['engagementSummaryChart', this.engagementTypesChart],
                    ['totalEngagementsChart', this.totalEngagementsChartModel],
                    ['engagementRateChart', this.engagementRateChartModel],
                    ['engagementTypeBySocialNetworkComparisonChart', this.engagementTypeBySocialNetworkComparisonChartModel]
                ]);
            })

        );
    }

    ngOnDestroy(): void {
        this.deregisterSeries();
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    private initializeAll(): void {
        this.socialMediaToDisplayMap = new Map<MediaTypeEnum, string>(
            getSocialMediaTypes().map(type => [type, this.localizationService.transform(`MediaType.${type}`)])
        );

        this.initializeCards();
        this.initializeEngagementsBySocialNetworkComparison();
        this.initializeEngagementTypeBySocialNetworkComparison();
        this.initializeCardsPerType();
        this.initializeEngagementTypesSeries();
        this.initializeEngamentCards();
        this.initializeTopPosts();
        this.initializeTopTerms();

        if (this.isArchiveSearch) {
            this.hideSeries = [
                // Top cards
                this.previousPeriodTotalPostsSeries,
                this.previousPeriodTotalEngagementsSeries,
                this.previousPeriodEngagementsPerPostsSeries,
                // cards per type
                this.previousPeriodPostsPerTypeSeries,
                this.previousPeriodTotalEngagementsPerTypeSeries,
                this.previousPeriodEngagementsRatesPerTypeSeries,
                // pie chart
                this.previousPeriodEngagementTypesSeries,
                // horizontal engagement cards
                this.previousPeriodTotalLikesSeries,
                this.previousPeriodTotalQuotesSeries,
                this.previousPeriodTotalRepliesSeries,
                this.previousPeriodTotalRetweetsSeries,
                this.previousPeriodTotalViewsSeries,
            ];
        } else {
            this.hideSeries = [];
        }

        this.socialSeriesService.fulfillSeries(
            [
                // Top cards
                this.totalPostsSeries,
                this.previousPeriodTotalPostsSeries,
                this.totalEngagementsSeries,
                this.previousPeriodTotalEngagementsSeries,
                this.engagementsPerPostsSeries,
                this.previousPeriodEngagementsPerPostsSeries,
                // Engagements by Social Network Comparison
                this.totalEngagementsSeriesModel,
                // Engagement Rate by Social Network Comparison
                this.engagementRateSeriesModel,
                // Engagement Type By Social Network Comparison
                this.applauseSeriesModel,
                this.conversationSeriesModel,
                this.amplificationSeriesModel,
                // cards per type
                this.postsPerTypeSeries,
                this.previousPeriodPostsPerTypeSeries,
                this.engagementsPerTypeSeries,
                this.previousPeriodTotalEngagementsPerTypeSeries,
                this.engagementRatesPerTypeSeries,
                this.previousPeriodEngagementsRatesPerTypeSeries,
                // pie chart
                this.engagementTypesSeries,
                this.previousPeriodEngagementTypesSeries,
                // horizontal engagement cards
                this.totalLikesSeries,
                this.previousPeriodTotalLikesSeries,
                this.totalQuotesSeries,
                this.previousPeriodTotalQuotesSeries,
                this.totalRepliesSeries,
                this.previousPeriodTotalRepliesSeries,
                this.totalRetweetsSeries,
                this.previousPeriodTotalRetweetsSeries,
                this.totalViewsSeries,
                this.previousPeriodTotalViewsSeries,
                // Top posts
                this.topPostsSeries,
                // Top terms
                this.topTermsSeries,
            ].filter(x => !this.hideSeries.find(series => x === series))
        );

        this.hideSeries.forEach(series => series.dataProvider.next([]));
    }

    private deregisterSeries(): void {
        this.socialSeriesService.deregisterSeries(
            [
                // Top cards
                this.totalPostsSeries,
                this.previousPeriodTotalPostsSeries,
                this.totalEngagementsSeries,
                this.previousPeriodTotalEngagementsSeries,
                this.engagementsPerPostsSeries,
                this.previousPeriodEngagementsPerPostsSeries,
                // Engagements by Social Network Comparison
                this.totalEngagementsSeriesModel,
                // Engagement Rate by Social Network Comparison
                this.engagementRateSeriesModel,
                // Engagement Type By Social Network Comparison
                this.applauseSeriesModel,
                this.conversationSeriesModel,
                this.amplificationSeriesModel,
                // cards per type
                this.postsPerTypeSeries,
                this.previousPeriodPostsPerTypeSeries,
                this.engagementsPerTypeSeries,
                this.previousPeriodTotalEngagementsPerTypeSeries,
                this.engagementRatesPerTypeSeries,
                this.previousPeriodEngagementsRatesPerTypeSeries,
                // pie chart
                this.engagementTypesSeries,
                this.previousPeriodEngagementTypesSeries,
                // horizontal engagement cards
                this.totalLikesSeries,
                this.previousPeriodTotalLikesSeries,
                this.totalQuotesSeries,
                this.previousPeriodTotalQuotesSeries,
                this.totalRepliesSeries,
                this.previousPeriodTotalRepliesSeries,
                this.totalRetweetsSeries,
                this.previousPeriodTotalRetweetsSeries,
                this.totalViewsSeries,
                this.previousPeriodTotalViewsSeries,
                // Top posts
                this.topPostsSeries,
                // Top terms
                this.topTermsSeries,
            ].filter(x => !this.hideSeries?.find(series => x === series))
        );
    }

    setCurrentSocialType(selectedType: NetworkTypeEnum) {
        this.currentSocialType = selectedType;

        // we must create a new chart so the label changes
        this.createNewMultiSeriesPieChart();

        const socialNetworkSeries = [
            // Pie chart
            this.engagementTypesSeries,
            this.previousPeriodEngagementTypesSeries,
            // Cards
            this.postsPerTypeSeries,
            this.previousPeriodPostsPerTypeSeries,
            this.engagementsPerTypeSeries,
            this.previousPeriodTotalEngagementsPerTypeSeries,
            this.engagementRatesPerTypeSeries,
            this.previousPeriodEngagementsRatesPerTypeSeries,
            // horizontal engagement cards
            this.totalLikesSeries,
            this.previousPeriodTotalLikesSeries,
            this.totalQuotesSeries,
            this.previousPeriodTotalQuotesSeries,
            this.totalRepliesSeries,
            this.previousPeriodTotalRepliesSeries,
            this.totalRetweetsSeries,
            this.previousPeriodTotalRetweetsSeries,
            this.totalViewsSeries,
            this.previousPeriodTotalViewsSeries,
        ].filter(x => !this.hideSeries?.find(series => x === series));

        this.socialSeriesService.deregisterSeries(
            socialNetworkSeries
        );

        for (const singleSeries of socialNetworkSeries) {
            singleSeries.mentions.filters = new MentionSearchModel();
            singleSeries.mentions.filters.searchCriteria.query.includedMediaTypes = networkTypeToMediaTypeModelMap.get(this.currentSocialType);
        }
        this.socialSeriesService.fulfillSeries(
            socialNetworkSeries
        );
    }

    public initializeCards(): void {
        // total posts series setup
        this.totalPostsSeries = new ReportSeriesModel();
        this.totalPostsSeries.label = 'Social.intelligence.themeAnalytics.chart.totalPostsLabel';
        this.totalPostsSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.totalPostsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce((previousValue, bucket) => previousValue + bucket.count, 0) || 0;
            return [{
                count: count
            }];
        };
        // previous period total posts series setup
        this.previousPeriodTotalPostsSeries = new ReportSeriesModel();
        this.previousPeriodTotalPostsSeries.mentions.isPreviousPeriod = true;
        this.previousPeriodTotalPostsSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.previousPeriodTotalPostsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce((previousValue, bucket) => previousValue + bucket.count, 0) || 0;
            return [{
                count: count
            }];
        };
        // total engagement series setup
        this.totalEngagementsSeries = new ReportSeriesModel();
        this.totalEngagementsSeries.label = 'Social.intelligence.themeAnalytics.chart.totalEngagementsLabel';
        this.totalEngagementsSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.totalEngagementsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce(
                (previousValue, bucket) => previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeEngagements][0].count || 0,
                0
            ) || 0;
            return [{
                count: count
            }];
        };
        // previous period total engagement series setup
        this.previousPeriodTotalEngagementsSeries = new ReportSeriesModel();
        this.previousPeriodTotalEngagementsSeries.mentions.isPreviousPeriod = true;
        this.previousPeriodTotalEngagementsSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.previousPeriodTotalEngagementsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce(
                (previousValue, bucket) => previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeEngagements][0].count || 0,
                0
            );

            return [{
                count: count
            }];
        };

        // engagements per posts series setup
        this.engagementsPerPostsSeries = new ReportSeriesModel();
        this.engagementsPerPostsSeries.label = 'Social.intelligence.themeAnalytics.chart.engagementsPerPostsLabel';
        this.engagementsPerPostsSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.engagementsPerPostsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce((previousValue, bucket) => previousValue + bucket.count, 0) || 0;

            const engagementCount = neoFacet?.buckets.reduce(
                (previousValue, bucket) => previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeEngagements][0].count || 0,
                0
            );

            let engagementPerPosts = 0;
            if (count > 0) {
                engagementPerPosts = engagementCount / count;
            }

            return [{
                count: engagementPerPosts
            }];
        };
        // previous period engagements per posts series setup
        this.previousPeriodEngagementsPerPostsSeries = new ReportSeriesModel();
        this.previousPeriodEngagementsPerPostsSeries.mentions.isPreviousPeriod = true;
        this.previousPeriodEngagementsPerPostsSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.previousPeriodEngagementsPerPostsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce((previousValue, bucket) => previousValue + bucket.count, 0) || 0;

            const engagementCount = neoFacet?.buckets.reduce(
                (previousValue, bucket) => previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeEngagements][0].count || 0,
                0
            );

            let engagementPerPosts = 0;
            if (count > 0) {
                engagementPerPosts = engagementCount / count;
            }

            return [{
                count: engagementPerPosts
            }];
        };
    }

    private initializeEngagementsBySocialNetworkComparison(): void {
        this.totalEngagementsSeriesModel = new ReportSeriesModel();
        this.totalEngagementsSeriesModel.hasPreviousPeriod = !this.isArchiveSearch;
        this.totalEngagementsSeriesModel.label = this.localizationService.transform('Social.intelligence.themeAnalytics.currentPeriod');
        this.totalEngagementsSeriesModel.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.totalEngagementsSeriesModel.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            if (!neoFacet) {
                return [];
            }
            const bucketsWithAllMediaTypes = this.fillBucketWithAllMediaTypesForTotalEngagements(neoFacet.buckets);
            return bucketsWithAllMediaTypes.map(bucket => {
                const filterOverrides = new MentionSearchCriteriaModel();

                // map 'TWEET' for backwards compatibility
                if (bucket.termValue === 'TWEET') {
                    bucket.termValue = MediaTypeEnum.TWITTER;
                }

                filterOverrides.query.includedMediaTypes = [new MediaTypeModel(bucket.termValue as MediaTypeEnum)];
                filterOverrides.sortField = SearchCriteriaSortFieldEnum.engagement_total;

                let viewMentionUrl = '';
                let viewMentionUrlPreviousPeriod = '';
                try {
                    viewMentionUrl = this.totalEngagementsSeriesModel.getSocialSeriesUrl(filterOverrides);
                    viewMentionUrlPreviousPeriod = this.totalEngagementsSeriesModel.getSocialSeriesUrl(filterOverrides, true);
                } catch {}

                return <DataPointModel>{
                    id: bucket.termValue,
                    label: this.localizationService.transform(`MediaType.${bucket.termValue as MediaTypeEnum}`),
                    xValue: bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeEngagements][0].count,
                    viewMentionUrl: viewMentionUrl,
                    viewMentionUrlPreviousPeriod: viewMentionUrlPreviousPeriod
                };
                // Filter checks to remove reddit, or any series that don't have a viewMentionUrl (were filtered out)
            }).filter(dataPoint => dataPoint.id !== NetworkTypeEnum.reddit && dataPoint.viewMentionUrl !== '')
                .sort((a, b) => a.id.localeCompare(b.id));
        };

        const totalEngagementsChartStyles = new ChartStylesModel();
        totalEngagementsChartStyles.colorMap = {
            'barColor': this.defaultChartColors.otherColors.volume,
            'previousPeriodBarColor': this.defaultChartColors.otherColors.volumePrevious
        };
        totalEngagementsChartStyles.customPadding = {
            top: 20,
            right: 20,
            bottom: 10,
            left: 20
        };
        totalEngagementsChartStyles.chartHeight = 400;
        totalEngagementsChartStyles.titleFontSize = 18;
        totalEngagementsChartStyles.titleLabelColorc = this.defaultChartColors.otherColors.textColor;
        totalEngagementsChartStyles.subtitleFontSize = 14;
        totalEngagementsChartStyles.subtitleLabelColor = this.defaultChartColors.otherColors.textColor;
        totalEngagementsChartStyles.tooltipX = 40;

        this.totalEngagementsChartOptionsModel = {
            styles: totalEngagementsChartStyles,
            label: 'Label',
            previousPeriodLabel: this.localizationService.transform('Social.intelligence.themeAnalytics.previousPeriod'),
            trendIndicators: !this.isArchiveSearch,
            hidePreviousPeriod: this.isArchiveSearch,
            imageAsCategory: true,
            appBaseHref: this.appBaseHref,
            xValueLabel: '',
            percentLabel: null,
            tooltipHTML: this.socialChartTooltipService.getMultiSeriesSharedTooltipHTML(
                'totalEngagementsChart',
                '{value} <span style="visibility:{hasDataForPeriod}">({totalPercent}%)</span>',
                '{legendLabel}',
                '{icon}'
            ),
            isSocialStyles: true,
            categoryLabelMap: this.socialMediaToDisplayMap
        };

        this.totalEngagementsChartModel = new IconBarChartModel(
            [this.totalEngagementsSeriesModel],
            this.totalEngagementsChartOptionsModel,
            this.mockMetadata
        );

        this.engagementRateSeriesModel = new ReportSeriesModel();
        this.engagementRateSeriesModel.hasPreviousPeriod = !this.isArchiveSearch;
        this.engagementRateSeriesModel.label = this.localizationService.transform('Social.intelligence.themeAnalytics.currentPeriod');
        this.engagementRateSeriesModel.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.engagementRateSeriesModel.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            if (!neoFacet) {
                return [];
            }
            const bucketsWithAllMediaTypes = this.fillBucketWithAllMediaTypesForTotalEngagements(neoFacet.buckets);
            return bucketsWithAllMediaTypes.map(bucket => {
                const filterOverrides = new MentionSearchCriteriaModel();

                // map 'TWEET' for backwards compatibility
                if (bucket.termValue === 'TWEET') {
                    bucket.termValue = MediaTypeEnum.TWITTER;
                }

                filterOverrides.query.includedMediaTypes = [new MediaTypeModel(bucket.termValue as MediaTypeEnum)];
                filterOverrides.sortField = SearchCriteriaSortFieldEnum.engagement_rate;

                let viewMentionUrl = '';
                let viewMentionUrlPreviousPeriod = '';
                try {
                    viewMentionUrl = this.engagementRateSeriesModel.getSocialSeriesUrl(filterOverrides);
                    viewMentionUrlPreviousPeriod = this.engagementRateSeriesModel.getSocialSeriesUrl(filterOverrides, true);
                } catch {}

                const followersCount = bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeImpressions][0].count || 0;
                const engagementCount = bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeEngagements][0].count || 0;

                let engagementRate = 0;
                if (followersCount > 0) {
                    engagementRate = engagementCount / followersCount;
                }

                return <DataPointModel>{
                    id: bucket.termValue,
                    label: this.localizationService.transform(`MediaType.${bucket.termValue as MediaTypeEnum}`),
                    xValue: engagementRate,
                    viewMentionUrl: viewMentionUrl,
                    viewMentionUrlPreviousPeriod: viewMentionUrlPreviousPeriod
                };
                // Filter checks to remove reddit, or any series that don't have a viewMentionUrl (were filtered out)
            }).filter(dataPoint => dataPoint.id !== NetworkTypeEnum.reddit && dataPoint.viewMentionUrl !== '')
                .sort((a, b) => a.id.localeCompare(b.id));
        };

        const engagementRateChartStyles = new ChartStylesModel();
        engagementRateChartStyles.colorMap = {
            'barColor': this.defaultChartColors.otherColors.impressions,
            'previousPeriodBarColor': this.defaultChartColors.otherColors.impressionsPrevious
        };
        engagementRateChartStyles.customPadding = {
            top: 20,
            right: 20,
            bottom: 10,
            left: 20
        };
        engagementRateChartStyles.chartHeight = 400;
        engagementRateChartStyles.titleFontSize = 18;
        engagementRateChartStyles.titleLabelColorc = this.defaultChartColors.otherColors.textColor;
        engagementRateChartStyles.subtitleFontSize = 14;
        engagementRateChartStyles.subtitleLabelColor = this.defaultChartColors.otherColors.textColor;
        engagementRateChartStyles.tooltipX = 30;

        this.engagementRateChartOptionsModel = {
            styles: engagementRateChartStyles,
            label: 'Label',
            previousPeriodLabel: this.localizationService.transform('Social.intelligence.themeAnalytics.previousPeriod'),
            trendIndicators: !this.isArchiveSearch,
            hidePreviousPeriod: this.isArchiveSearch,
            imageAsCategory: true,
            appBaseHref: this.appBaseHref,
            xValueLabel: '',
            percentLabel: 'true',
            tooltipHTML: this.socialChartTooltipService.getMultiSeriesSharedTooltipHTML(
                'engagementRateChart',
                '{value} <span style="visibility:{hasDataForPeriod}"></span>',
                '{legendLabel}',
                '{icon}'
            ),
            isSocialStyles: true,
            categoryLabelMap: this.socialMediaToDisplayMap
        };

        this.engagementRateChartModel = new IconBarChartModel(
            [this.engagementRateSeriesModel],
            this.engagementRateChartOptionsModel,
            this.mockMetadata
        );
    }

    private initializeEngagementTypeBySocialNetworkComparison(): void {
        this.applauseSeriesModel = new ReportSeriesModel();
        this.applauseSeriesModel.hasPreviousPeriod = !this.isArchiveSearch;
        this.applauseSeriesModel.label = this.localizationService.transform(`Social.intelligence.themeAnalytics.engagement.${EngagementTypeEnum.applause}`);
        this.applauseSeriesModel.mentions.facetGenerator = () => {
            return {
                facet: engagementsNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.applauseSeriesModel.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.totalEngagements);
            if (!neoFacet) {
                return [];
            }
            const bucketsWithAllMediaTypes = this.fillBucketWithAllMediaTypesForIndividualEngagements(neoFacet.buckets);
            return bucketsWithAllMediaTypes.map(bucket => {
                const filterOverrides = new MentionSearchCriteriaModel();

                // map 'TWEET' for backwards compatibility
                if (bucket.termValue === 'TWEET') {
                    bucket.termValue = MediaTypeEnum.TWITTER;
                }

                filterOverrides.query.includedMediaTypes = [new MediaTypeModel(bucket.termValue as MediaTypeEnum)];
                filterOverrides.sortField = SearchCriteriaSortFieldEnum.engagement_total;

                let viewMentionUrl = '';
                let viewMentionUrlPreviousPeriod = '';
                try {
                    viewMentionUrl = this.applauseSeriesModel.getSocialSeriesUrl(filterOverrides);
                    viewMentionUrlPreviousPeriod = this.applauseSeriesModel.getSocialSeriesUrl(filterOverrides, true);

                } catch {}

                return <DataPointModel>{
                    id: bucket.termValue,
                    label: this.localizationService.transform(`MediaType.${bucket.termValue as MediaTypeEnum}`),
                    xValue: bucket.buckets[SocialSeriesNeoFacetKeysEnum.engagementTotalLikes][0].count,
                    viewMentionUrl,
                    viewMentionUrlPreviousPeriod
                };
                // Filter checks to remove reddit, or any series that don't have a viewMentionUrl (were filtered out)
            }).filter(dataPoint => dataPoint.id !== NetworkTypeEnum.reddit && dataPoint.viewMentionUrl !== '')
                .sort((a, b) => a.id.localeCompare(b.id));
        };

        const applauseChartStyles = new ChartStylesModel();
        applauseChartStyles.colorMap = {
            'barColor': this.defaultChartColors.otherColors.volume,
            'previousPeriodBarColor': this.defaultChartColors.otherColors.volumePrevious
        };
        applauseChartStyles.customPadding = {
            top: 20,
            right: 0,
            bottom: 10,
            left: 5
        };
        applauseChartStyles.chartHeight = 400;
        applauseChartStyles.titleFontSize = 18;
        applauseChartStyles.titleLabelColorc = this.defaultChartColors.otherColors.textColor;
        applauseChartStyles.subtitleFontSize = 14;
        applauseChartStyles.subtitleLabelColor = this.defaultChartColors.otherColors.textColor;

        const applauseChartOptionsModel = {
            styles: applauseChartStyles,
            label: 'Label',
            imageAsCategory: true,
            previousPeriodLabel: this.localizationService.transform(`Social.intelligence.themeAnalytics.engagement.${EngagementTypeEnum.applause}`) +
                '(' + this.localizationService.transform('Social.intelligence.themeAnalytics.previousPeriod') + ')',
            trendIndicators: !this.isArchiveSearch,
            hidePreviousPeriod: this.isArchiveSearch,
            appBaseHref: this.appBaseHref,
            xValueLabel: '',
            percentLabel: null,
            tooltipHTML: this.socialChartTooltipService.getMultiSeriesSharedTooltipHTML(
                'engagementTypeBySocialNetworkComparisonChart',
                '{value} <span style="visibility:{hasDataForPeriod}">({totalPercent}%)</span>',
                '{legendLabel}',
                '{icon}'
            ),
            isSocialStyles: true,
            categoryLabelMap: this.socialMediaToDisplayMap
        };

        const applauseChartModel = new IconBarChartModel(
            [this.applauseSeriesModel],
            applauseChartOptionsModel,
            this.mockMetadata
        );

        this.conversationSeriesModel = new ReportSeriesModel();
        this.conversationSeriesModel.hasPreviousPeriod = !this.isArchiveSearch;
        this.conversationSeriesModel.label =
            this.localizationService.transform(`Social.intelligence.themeAnalytics.engagement.${EngagementTypeEnum.conversation}`);
        this.conversationSeriesModel.mentions.facetGenerator = () => {
            return {
                facet: engagementsNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.conversationSeriesModel.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.totalEngagements);
            if (!neoFacet) {
                return [];
            }
            const bucketsWithAllMediaTypes = this.fillBucketWithAllMediaTypesForIndividualEngagements(neoFacet.buckets);
            return bucketsWithAllMediaTypes.map(bucket => {
                const filterOverrides = new MentionSearchCriteriaModel();

                // map 'TWEET' for backwards compatibility
                if (bucket.termValue === 'TWEET') {
                    bucket.termValue = MediaTypeEnum.TWITTER;
                }

                filterOverrides.query.includedMediaTypes = [new MediaTypeModel(bucket.termValue as MediaTypeEnum)];
                filterOverrides.sortField = SearchCriteriaSortFieldEnum.engagement_total;

                let viewMentionUrl = '';
                let viewMentionUrlPreviousPeriod = '';
                try {
                    viewMentionUrl = this.conversationSeriesModel.getSocialSeriesUrl(filterOverrides);
                    viewMentionUrlPreviousPeriod = this.conversationSeriesModel.getSocialSeriesUrl(filterOverrides, true);
                } catch {}

                return <DataPointModel>{
                    id: bucket.termValue,
                    label: this.localizationService.transform(`MediaType.${bucket.termValue as MediaTypeEnum}`),
                    xValue: bucket.buckets[SocialSeriesNeoFacetKeysEnum.engagementTotalReply][0].count +
                        bucket.buckets[SocialSeriesNeoFacetKeysEnum.engagementTotalQuotes][0].count,
                    viewMentionUrl,
                    viewMentionUrlPreviousPeriod
                };
                // Filter checks to remove reddit, or any series that don't have a viewMentionUrl (were filtered out)
            }).filter(dataPoint => dataPoint.id !== NetworkTypeEnum.reddit && dataPoint.viewMentionUrl !== '')
                .sort((a, b) => a.id.localeCompare(b.id));
        };

        const conversationChartStyles = new ChartStylesModel();
        conversationChartStyles.colorMap = {
            'barColor': this.defaultChartColors.otherColors.impressions,
            'previousPeriodBarColor': this.defaultChartColors.otherColors.impressionsPrevious
        };
        conversationChartStyles.customPadding = {
            top: 20,
            right: 0,
            bottom: 10,
            left: 5
        };
        conversationChartStyles.chartHeight = 400;
        conversationChartStyles.titleFontSize = 18;
        conversationChartStyles.titleLabelColorc = this.defaultChartColors.otherColors.textColor;
        conversationChartStyles.subtitleFontSize = 14;
        conversationChartStyles.subtitleLabelColor = this.defaultChartColors.otherColors.textColor;

        const conversationChartOptionsModel = {
            styles: conversationChartStyles,
            label: 'Label',
            imageAsCategory: true,
            appBaseHref: this.appBaseHref,
            xValueLabel: '',
            previousPeriodLabel: this.localizationService.transform(`Social.intelligence.themeAnalytics.engagement.${EngagementTypeEnum.conversation}`) +
                '(' + this.localizationService.transform('Social.intelligence.themeAnalytics.previousPeriod') + ')',
            trendIndicators: !this.isArchiveSearch,
            hidePreviousPeriod: this.isArchiveSearch,
            percentLabel: null,
            tooltipHTML: this.socialChartTooltipService.getMultiSeriesSharedTooltipHTML(
                'engagementTypeBySocialNetworkComparisonChart',
                '{value} <span style="visibility:{hasDataForPeriod}">({totalPercent}%)</span>',
                '{legendLabel}',
                '{icon}'
            ),
            isSocialStyles: true,
            categoryLabelMap: this.socialMediaToDisplayMap
        };

        const conversationChartModel = new IconBarChartModel(
            [this.conversationSeriesModel],
            conversationChartOptionsModel,
            this.mockMetadata
        );

        this.amplificationSeriesModel = new ReportSeriesModel();
        this.amplificationSeriesModel.hasPreviousPeriod = !this.isArchiveSearch;
        this.amplificationSeriesModel.label =
            this.localizationService.transform(`Social.intelligence.themeAnalytics.engagement.${EngagementTypeEnum.amplification}`);
        this.amplificationSeriesModel.mentions.facetGenerator = () => {
            return {
                facet: engagementsNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.amplificationSeriesModel.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.totalEngagements);
            if (!neoFacet) {
                return [];
            }
            const bucketsWithAllMediaTypes = this.fillBucketWithAllMediaTypesForIndividualEngagements(neoFacet.buckets);
            return bucketsWithAllMediaTypes.map(bucket => {
                const filterOverrides = new MentionSearchCriteriaModel();

                // map 'TWEET' for backwards compatibility
                if (bucket.termValue === 'TWEET') {
                    bucket.termValue = MediaTypeEnum.TWITTER;
                }

                filterOverrides.query.includedMediaTypes = [new MediaTypeModel(bucket.termValue as MediaTypeEnum)];
                filterOverrides.sortField = SearchCriteriaSortFieldEnum.engagement_total;

                let viewMentionUrl = '';
                let viewMentionUrlPreviousPeriod = '';
                try {
                    viewMentionUrl = this.amplificationSeriesModel.getSocialSeriesUrl(filterOverrides);
                    viewMentionUrlPreviousPeriod = this.amplificationSeriesModel.getSocialSeriesUrl(filterOverrides, true);
                } catch {}

                return <DataPointModel>{
                    id: bucket.termValue,
                    label: this.localizationService.transform(`MediaType.${bucket.termValue as MediaTypeEnum}`),
                    xValue: bucket.buckets[SocialSeriesNeoFacetKeysEnum.engagementTotalShare][0].count,
                    viewMentionUrl,
                    viewMentionUrlPreviousPeriod
                };
                // Filter checks to remove reddit, or any series that don't have a viewMentionUrl (were filtered out)
            }).filter(dataPoint => dataPoint.id !== NetworkTypeEnum.reddit && dataPoint.viewMentionUrl !== '')
                .sort((a, b) => a.id.localeCompare(b.id));
        };

        const amplificationChartStyles = new ChartStylesModel();
        amplificationChartStyles.colorMap = {
            'barColor': this.defaultChartColors.otherColors.engagements,
            'previousPeriodBarColor': this.defaultChartColors.otherColors.engagementPrevious
        };
        amplificationChartStyles.customPadding = {
            top: 20,
            right: 0,
            bottom: 10,
            left: 5
        };
        amplificationChartStyles.chartHeight = 400;
        amplificationChartStyles.titleFontSize = 18;
        amplificationChartStyles.titleLabelColorc = this.defaultChartColors.otherColors.textColor;
        amplificationChartStyles.subtitleFontSize = 14;
        amplificationChartStyles.subtitleLabelColor = this.defaultChartColors.otherColors.textColor;

        const amplificationChartOptionsModel = {
            styles: amplificationChartStyles,
            label: 'Label',
            imageAsCategory: true,
            appBaseHref: this.appBaseHref,
            xValueLabel: '',
            previousPeriodLabel: this.localizationService.transform(`Social.intelligence.themeAnalytics.engagement.${EngagementTypeEnum.amplification}`) +
                '(' + this.localizationService.transform('Social.intelligence.themeAnalytics.previousPeriod') + ')',
            trendIndicators: !this.isArchiveSearch,
            hidePreviousPeriod: this.isArchiveSearch,
            percentLabel: null,
            tooltipHTML: this.socialChartTooltipService.getMultiSeriesSharedTooltipHTML(
                'engagementTypeBySocialNetworkComparisonChart',
                '{value} <span style="visibility:{hasDataForPeriod}">({totalPercent}%)</span>',
                '{legendLabel}',
                '{icon}'
            ),
            isSocialStyles: true,
            categoryLabelMap: this.socialMediaToDisplayMap
        };

        const amplificationChartModel = new IconBarChartModel(
            [this.amplificationSeriesModel],
            amplificationChartOptionsModel,
            this.mockMetadata
        );

        const engagementTypeBySocialNetworkComparisonChartStyles = new ChartStylesModel();
        engagementTypeBySocialNetworkComparisonChartStyles.height = 425;

        this.engagementTypeBySocialNetworkComparisonChartModel = new ChartGroupInputModel(
            [applauseChartModel, conversationChartModel, amplificationChartModel],
            {
                styles: engagementTypeBySocialNetworkComparisonChartStyles,
                noPadding: true
            },
            {
                type: 'bar',
                titleText: '',
                dateRange: null,
                dateRangeLabelText: '',
                labelDisplayOptions: [],
                enableDataDisplaySettings: false,
                noContentLabel: this.mockMetadata.noContentLabel
            }
        );
    }

    public initializeCardsPerType(): void {
        this.engagementSummaryExportData.label = this.currentSocialType.toUpperCase();
        // post per type
        this.postsPerTypeSeries = new ReportSeriesModel();
        this.postsPerTypeSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.postsPerTypeSeries.mentions.filters = new MentionSearchModel();
        this.postsPerTypeSeries.mentions.filters.searchCriteria.query.includedMediaTypes
            = networkTypeToMediaTypeModelMap.get(this.currentSocialType);
        this.postsPerTypeSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce((previousValue, bucket) => previousValue + bucket.count, 0) || 0;
            this.engagementSummaryExportData.posts = count;
            return [{
                count: count
            }];
        };
        // previous period post per type
        this.previousPeriodPostsPerTypeSeries = new ReportSeriesModel();
        this.previousPeriodPostsPerTypeSeries.mentions.isPreviousPeriod = true;
        this.previousPeriodPostsPerTypeSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.previousPeriodPostsPerTypeSeries.mentions.filters = new MentionSearchModel();
        this.previousPeriodPostsPerTypeSeries.mentions.filters.searchCriteria.query.includedMediaTypes
            = networkTypeToMediaTypeModelMap.get(this.currentSocialType);
        this.previousPeriodPostsPerTypeSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce((previousValue, bucket) => previousValue + bucket.count, 0) || 0;
            this.engagementSummaryExportData.previousPeriodPosts = count;
            return [{
                count: count
            }];
        };

        // engagements per type
        this.engagementsPerTypeSeries = new ReportSeriesModel();
        this.engagementsPerTypeSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.engagementsPerTypeSeries.mentions.filters = new MentionSearchModel();
        this.engagementsPerTypeSeries.mentions.filters.searchCriteria.query.includedMediaTypes
            = networkTypeToMediaTypeModelMap.get(this.currentSocialType);
        this.engagementsPerTypeSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce(
                (previousValue, bucket) => previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeEngagements][0].count,
                0
            ) || 0;
            this.engagementSummaryExportData.engagements = count;
            return [{
                count: count
            }];
        };
        // previous period engagements per type
        this.previousPeriodTotalEngagementsPerTypeSeries = new ReportSeriesModel();
        this.previousPeriodTotalEngagementsPerTypeSeries.mentions.isPreviousPeriod = true;
        this.previousPeriodTotalEngagementsPerTypeSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.previousPeriodTotalEngagementsPerTypeSeries.mentions.filters = new MentionSearchModel();
        this.previousPeriodTotalEngagementsPerTypeSeries.mentions.filters.searchCriteria.query.includedMediaTypes =
            networkTypeToMediaTypeModelMap.get(this.currentSocialType);
        this.previousPeriodTotalEngagementsPerTypeSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce(
                (previousValue, bucket) => previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeEngagements][0].count,
                0
            ) || 0;
            this.engagementSummaryExportData.previousPeriodEngagements = count;
            return [{
                count: count
            }];
        };

        // engagement rates per type
        this.engagementRatesPerTypeSeries = new ReportSeriesModel();
        this.engagementRatesPerTypeSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.engagementRatesPerTypeSeries.mentions.filters = new MentionSearchModel();
        this.engagementRatesPerTypeSeries.mentions.filters.searchCriteria.query.includedMediaTypes
            = networkTypeToMediaTypeModelMap.get(this.currentSocialType);
        this.engagementRatesPerTypeSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);

            const followersCount = neoFacet?.buckets.reduce(
                (previousValue, bucket) => previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeImpressions][0].count || 0,
                0
            );
            const engagementCount = neoFacet?.buckets.reduce(
                (previousValue, bucket) => previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeEngagements][0].count || 0,
                0
            );

            let engagementRate = 0;
            if (followersCount > 0) {
                engagementRate = engagementCount / followersCount;
            }
            this.engagementSummaryExportData.engagementRate = engagementRate * 100;
            return [{
                count: engagementRate * 100
            }];
        };
        // previous period engagement rates per type
        this.previousPeriodEngagementsRatesPerTypeSeries = new ReportSeriesModel();
        this.previousPeriodEngagementsRatesPerTypeSeries.mentions.isPreviousPeriod = true;
        this.previousPeriodEngagementsRatesPerTypeSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.previousPeriodEngagementsRatesPerTypeSeries.mentions.filters = new MentionSearchModel();
        this.previousPeriodEngagementsRatesPerTypeSeries.mentions.filters.searchCriteria.query.includedMediaTypes =
            networkTypeToMediaTypeModelMap.get(this.currentSocialType);
        this.previousPeriodEngagementsRatesPerTypeSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const followersCount = neoFacet?.buckets.reduce(
                (previousValue, bucket) => previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeImpressions][0].count || 0,
                0
            );
            const engagementCount = neoFacet?.buckets.reduce(
                (previousValue, bucket) => previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeEngagements][0].count || 0,
                0
            );

            let engagementRate = 0;
            if (followersCount > 0) {
                engagementRate = engagementCount / followersCount;
            }
            this.engagementSummaryExportData.previousPeriodEngagementRate = engagementRate * 100;
            return [{
                count: engagementRate * 100
            }];
        };
    }

    public initializeEngamentCards(): void {
        // likes per social type
        this.totalLikesSeries = new ReportSeriesModel();
        this.totalLikesSeries.mentions.facetGenerator = () => {
            return {
                facet: engagementsNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.totalLikesSeries.mentions.filters = new MentionSearchModel();
        this.totalLikesSeries.mentions.filters.searchCriteria.query.includedMediaTypes
            = networkTypeToMediaTypeModelMap.get(this.currentSocialType);
        this.totalLikesSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.totalEngagements);
            const count = neoFacet?.buckets.reduce(
                (previousValue, bucket) => previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.engagementTotalLikes][0].count,
                0
            ) || 0;
            this.engagementSummaryExportData.likes = count;
            return [{
                count: count
            }];
        };
        // previous period likes per social type
        this.previousPeriodTotalLikesSeries = new ReportSeriesModel();
        this.previousPeriodTotalLikesSeries.mentions.isPreviousPeriod = true;
        this.previousPeriodTotalLikesSeries.mentions.facetGenerator = () => {
            return {
                facet: engagementsNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.previousPeriodTotalLikesSeries.mentions.filters = new MentionSearchModel();
        this.previousPeriodTotalLikesSeries.mentions.filters.searchCriteria.query.includedMediaTypes
            = networkTypeToMediaTypeModelMap.get(this.currentSocialType);
        this.previousPeriodTotalLikesSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.totalEngagements);
            const count = neoFacet?.buckets.reduce(
                (previousValue, bucket) => previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.engagementTotalLikes][0].count,
                0
            ) || 0;
            return [{
                count: count
            }];
        };

        // quotes per social type
        this.totalQuotesSeries = new ReportSeriesModel();
        this.totalQuotesSeries.label = 'Social.intelligence.themeAnalytics.engagement.quotes';
        this.totalQuotesSeries.mentions.facetGenerator = () => {
            return {
                facet: engagementsNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.totalQuotesSeries.mentions.filters = new MentionSearchModel();
        this.totalQuotesSeries.mentions.filters.searchCriteria.query.includedMediaTypes
            = networkTypeToMediaTypeModelMap.get(this.currentSocialType);
        this.totalQuotesSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.totalEngagements);
            const count = neoFacet?.buckets.reduce(
                (previousValue, bucket) => previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.engagementTotalQuotes][0].count,
                0
            ) || 0;
            this.engagementSummaryExportData.quotes = count;
            return [{
                count: count
            }];
        };
        // previous period quotes per social type
        this.previousPeriodTotalQuotesSeries = new ReportSeriesModel();
        this.previousPeriodTotalQuotesSeries.mentions.isPreviousPeriod = true;
        this.previousPeriodTotalQuotesSeries.mentions.facetGenerator = () => {
            return {
                facet: engagementsNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.previousPeriodTotalQuotesSeries.mentions.filters = new MentionSearchModel();
        this.previousPeriodTotalQuotesSeries.mentions.filters.searchCriteria.query.includedMediaTypes
            = networkTypeToMediaTypeModelMap.get(this.currentSocialType);
        this.previousPeriodTotalQuotesSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.totalEngagements);
            const count = neoFacet?.buckets.reduce(
                (previousValue, bucket) => previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.engagementTotalQuotes][0].count,
                0
            ) || 0;
            return [{
                count: count
            }];
        };

        // replies per social type
        this.totalRepliesSeries = new ReportSeriesModel();
        this.totalRepliesSeries.mentions.facetGenerator = () => {
            return {
                facet: engagementsNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.totalRepliesSeries.mentions.filters = new MentionSearchModel();
        this.totalRepliesSeries.mentions.filters.searchCriteria.query.includedMediaTypes
            = networkTypeToMediaTypeModelMap.get(this.currentSocialType);
        this.totalRepliesSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.totalEngagements);
            const count = neoFacet?.buckets.reduce(
                (previousValue, bucket) => previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.engagementTotalReply][0].count,
                0
            ) || 0;
            this.engagementSummaryExportData.replies = count;
            return [{
                count: count
            }];
        };
        // previous period replies per social type
        this.previousPeriodTotalRepliesSeries = new ReportSeriesModel();
        this.previousPeriodTotalRepliesSeries.mentions.isPreviousPeriod = true;
        this.previousPeriodTotalRepliesSeries.mentions.facetGenerator = () => {
            return {
                facet: engagementsNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.previousPeriodTotalRepliesSeries.mentions.filters = new MentionSearchModel();
        this.previousPeriodTotalRepliesSeries.mentions.filters.searchCriteria.query.includedMediaTypes
            = networkTypeToMediaTypeModelMap.get(this.currentSocialType);
        this.previousPeriodTotalRepliesSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.totalEngagements);
            const count = neoFacet?.buckets.reduce(
                (previousValue, bucket) => previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.engagementTotalReply][0].count,
                0
            ) || 0;
            return [{
                count: count
            }];
        };

        // retweets per social type
        this.totalRetweetsSeries = new ReportSeriesModel();
        this.totalRetweetsSeries.mentions.facetGenerator = () => {
            return {
                facet: engagementsNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.totalRetweetsSeries.mentions.filters = new MentionSearchModel();
        this.totalRetweetsSeries.mentions.filters.searchCriteria.query.includedMediaTypes
             = networkTypeToMediaTypeModelMap.get(this.currentSocialType);
        this.totalRetweetsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.totalEngagements);
            const count = neoFacet?.buckets.reduce(
                (previousValue, bucket) => previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.engagementTotalShare][0].count,
                0
            ) || 0;
            this.engagementSummaryExportData.quotes = count;
            return [{
                count: count
            }];
        };
        // previous period retweets per social type
        this.previousPeriodTotalRetweetsSeries = new ReportSeriesModel();
        this.previousPeriodTotalRetweetsSeries.mentions.isPreviousPeriod = true;
        this.previousPeriodTotalRetweetsSeries.mentions.facetGenerator = () => {
            return {
                facet: engagementsNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.previousPeriodTotalRetweetsSeries.mentions.filters = new MentionSearchModel();
        this.previousPeriodTotalRetweetsSeries.mentions.filters.searchCriteria.query.includedMediaTypes
             = networkTypeToMediaTypeModelMap.get(this.currentSocialType);
        this.previousPeriodTotalRetweetsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.totalEngagements);
            const count = neoFacet?.buckets.reduce(
                (previousValue, bucket) => previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.engagementTotalShare][0].count,
                0
            ) || 0;
            return [{
                count: count
            }];
        };

        // views per social type
        this.totalViewsSeries = new ReportSeriesModel();
        this.totalViewsSeries.mentions.facetGenerator = () => {
            return {
                facet: engagementsNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.totalViewsSeries.mentions.filters = new MentionSearchModel();
        this.totalViewsSeries.mentions.filters.searchCriteria.query.includedMediaTypes
            = networkTypeToMediaTypeModelMap.get(this.currentSocialType);
        this.totalViewsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.totalEngagements);
            const count = neoFacet?.buckets.reduce(
                (previousValue, bucket) => previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.engagementTotalView][0].count,
                0
            ) || 0;
            this.engagementSummaryExportData.views = count;
            return [{
                count: count
            }];
        };
        // previous period views per social type
        this.previousPeriodTotalViewsSeries = new ReportSeriesModel();
        this.previousPeriodTotalViewsSeries.mentions.isPreviousPeriod = true;
        this.previousPeriodTotalViewsSeries.mentions.facetGenerator = () => {
            return {
                facet: engagementsNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.previousPeriodTotalViewsSeries.mentions.filters = new MentionSearchModel();
        this.previousPeriodTotalViewsSeries.mentions.filters.searchCriteria.query.includedMediaTypes
            = networkTypeToMediaTypeModelMap.get(this.currentSocialType);
        this.previousPeriodTotalViewsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.totalEngagements);
            const count = neoFacet?.buckets.reduce(
                (previousValue, bucket) => previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.engagementTotalView][0].count,
                0
            ) || 0;
            return [{
                count: count
            }];
        };
    }

    public initializeEngagementTypesSeries() {
        this.engagementTypesSeries = new ReportSeriesModel();
        this.engagementTypesSeries.mentions.facetGenerator = () => {
            return {
                facet: engagementsNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.engagementTypesSeries.mentions.filters.searchCriteria.query.includedMediaTypes = networkTypeToMediaTypeModelMap.get(this.currentSocialType);
        this.engagementTypesSeries.mentions.dataMapper = this.getEngagementMapper(this.engagementTypesSeries);

        this.previousPeriodEngagementTypesSeries = new ReportSeriesModel();
        this.previousPeriodEngagementTypesSeries.label = this.localizationService.transform('Social.intelligence.themeAnalytics.previousPeriod');
        this.previousPeriodEngagementTypesSeries.mentions.isPreviousPeriod = true;
        this.previousPeriodEngagementTypesSeries.mentions.filters.searchCriteria.query.includedMediaTypes =
            networkTypeToMediaTypeModelMap.get(this.currentSocialType);
        this.previousPeriodEngagementTypesSeries.mentions.facetGenerator = () => {
            return {
                facet: engagementsNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.previousPeriodEngagementTypesSeries.mentions.dataMapper = this.getEngagementMapper(this.previousPeriodEngagementTypesSeries);

        this.createNewMultiSeriesPieChart();
    }

    createNewMultiSeriesPieChart() {
        const mockOptions = this.themeAnalyticsChartOptionsService.getChartOptions(ThemeAnalyticsChartTypeEnum.SOCIAL_MEDIA_ENGAGEMENT);
        mockOptions.legendContainerElement = this.engagementProfileChartLegend.nativeElement;

        const mockMetadata = {
            titleText: this.localizationService.transform(`Social.intelligence.themeAnalytics.engagement.${this.currentSocialType}.engagementProfile`),
            dateRange: {
                type: 'RELATIVE',
                relativeStartOffsetH: -24,
                relativeEndOffsetH: 0
            },
            labelDisplayOptions: [
                'count'
            ],
            noContentLabel: this.mockMetadata.noContentLabel,
            noDataFromDateRangeLabel: this.localizationService.transform('Social.intelligence.themeAnalytics.chart.noDataFromDateRange'),
            enableDataDisplaySettings: false,
            titleHeadingLevel: 5
        } as ChartMetadataModel;

        this.engagementTypesChart = new MultiSeriesPieChartModel(
            this.isArchiveSearch ? [this.engagementTypesSeries] : [this.previousPeriodEngagementTypesSeries, this.engagementTypesSeries],
            mockOptions,
            mockMetadata
        );
    }

    public initializeTopPosts(): void {
        this.topPostsSeries = new ReportSeriesModel();
        this.topPostsSeries.mentionProvider = new BehaviorSubject<Mention[]>(null);
        this.topPostsSortField = SearchCriteriaSortFieldEnum.audience_reach;
        this.topPostsSeries.mentions.mentionRequestGenerator = () => {
            return {
                sortOrder: SortOrderEnum.descending,
                sortField: SearchCriteriaSortFieldEnum.engagement_total,
                pageSize: 5
            };
        };
        this.topPostsSeries.mentions.mentionDataMapper = (response) => {
            return response.mentions.map(apiMention => new Mention(apiMention));
        };
        this.topPostsSeries.mentions.filters = new MentionSearchModel();
    }

    public initializeTopTerms(): void {
        this.topTermsSortField = SearchCriteriaSortFieldEnum.audience_reach;
        this.topTermsSeries = new ReportSeriesModel();
        this.topTermsSeries.mentions.facetGenerator = () => {
            return {
                facet: mentionsByEngagementNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.topTermsSeries.mentions.filters = new MentionSearchModel();
        this.topTermsSeries.mentions.dataMapper = TopTermsDataMappers.neofacetEngagementDataMapper(this.topTermsSeries, true);
    }

    private getEngagementMapper(series: ReportSeriesModel) {
        return (response) => {
            const filterOverrides = new MentionSearchCriteriaModel();
            filterOverrides.query.includedMediaTypes = networkTypeToMediaTypeModelMap.get(this.currentSocialType);
            filterOverrides.sortField = SearchCriteriaSortFieldEnum.engagement_total;

            const returnArray: DataPointModel[] = [
                {
                    id: EngagementTypeEnum.applause,
                    label: this.localizationService.transform(`Social.intelligence.themeAnalytics.engagement.${EngagementTypeEnum.applause}`),
                    xValue: 0,
                    viewMentionUrl: series.getSocialSeriesUrl(filterOverrides)
                },
                {
                    id: EngagementTypeEnum.conversation,
                    label: this.localizationService.transform(`Social.intelligence.themeAnalytics.engagement.${EngagementTypeEnum.conversation}`),
                    xValue: 0,
                    viewMentionUrl: series.getSocialSeriesUrl(filterOverrides)
                },
                {
                    id: EngagementTypeEnum.amplification,
                    label: this.localizationService.transform(`Social.intelligence.themeAnalytics.engagement.${EngagementTypeEnum.amplification}`),
                    xValue: 0,
                    viewMentionUrl: series.getSocialSeriesUrl(filterOverrides)
                },
            ];

            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.totalEngagements);

            neoFacet?.buckets.map(
                (bucket) => {
                    // applause
                    returnArray[0].xValue += bucket.buckets[SocialSeriesNeoFacetKeysEnum.engagementTotalLikes][0].count || 0;

                    // conversation
                    returnArray[1].xValue += bucket.buckets[SocialSeriesNeoFacetKeysEnum.engagementTotalQuotes][0].count || 0;
                    returnArray[1].xValue += bucket.buckets[SocialSeriesNeoFacetKeysEnum.engagementTotalReply][0].count || 0;

                    // amplification
                    returnArray[2].xValue += bucket.buckets[SocialSeriesNeoFacetKeysEnum.engagementTotalShare][0].count || 0;

                    return bucket;
                }
            );

            return returnArray;
        };
    }

    private fillBucketWithAllMediaTypesForTotalEngagements(original: BucketResponseModel[]): BucketResponseModel[]{
        const buckets = Array.from(original);
        const allMediaTypes: MediaTypeEnum[] = this.enabledNetworkTypes
            .filter(network => network !== NetworkTypeEnum.all && network !== NetworkTypeEnum.subReddit)
            .map(network => networkTypeToMediaTypeMap.get(network))
            .filter(network => !!network);

        if (buckets.length !== allMediaTypes.length) {
            buckets.forEach(
                bucket => {
                    allMediaTypes.splice(allMediaTypes.indexOf(bucket.termValue as MediaTypeEnum), 1);
                }
            );
            if (allMediaTypes.length !== 0) {
                allMediaTypes.forEach(
                    type => {
                        buckets.push({
                            termValue: type,
                            count: 0,
                            buckets: {
                                [SocialSeriesNeoFacetKeysEnum.mediaTypeImpressions] : [{
                                    buckets: {},
                                    count: 0
                                }],
                                [SocialSeriesNeoFacetKeysEnum.mediaTypeEngagements] : [{
                                    buckets: {},
                                    count: 0
                                }]
                            }
                        });
                    }
                );
            }
        }
        return buckets;
    }

    private fillBucketWithAllMediaTypesForIndividualEngagements(original: BucketResponseModel[]): BucketResponseModel[] {
        const buckets = Array.from(original);
        const allMediaTypes: MediaTypeEnum[] = this.enabledNetworkTypes
            .filter(network => network !== NetworkTypeEnum.all && network !== NetworkTypeEnum.subReddit)
            .map(network => networkTypeToMediaTypeMap.get(network))
            .filter(network => !!network);

        if (buckets.length !== allMediaTypes.length) {
            buckets.forEach(
                bucket => {
                    allMediaTypes.splice(allMediaTypes.indexOf(bucket.termValue as MediaTypeEnum), 1);
                }
            );
            if (allMediaTypes.length !== 0) {
                allMediaTypes.forEach(
                    type => {
                        buckets.push({
                            termValue: type,
                            count: 0,
                            buckets: {
                                [SocialSeriesNeoFacetKeysEnum.engagementTotalLikes] : [{
                                    buckets: {},
                                    count: 0
                                }],
                                [SocialSeriesNeoFacetKeysEnum.engagementTotalQuotes] : [{
                                    buckets: {},
                                    count: 0
                                }],
                                [SocialSeriesNeoFacetKeysEnum.engagementTotalReply] : [{
                                    buckets: {},
                                    count: 0
                                }],
                                [SocialSeriesNeoFacetKeysEnum.engagementTotalShare] : [{
                                    buckets: {},
                                    count: 0
                                }],
                                [SocialSeriesNeoFacetKeysEnum.engagementTotalView] : [{
                                    buckets: {},
                                    count: 0
                                }],
                            }
                        });
                    }
                );
            }
        }
        return buckets;
    }

    public exportChart(event: ChartExportEventModel, elementRef: any, chartType?: ThemeAnalyticsChartTypeEnum) {
        const exportType = event.eventKey.toLowerCase() as keyof am4core.IExportOptions;
        const exportModel = this.prepareExportData(chartType);
        if (exportType === 'csv' || exportType === 'json') {
            this.chartExportService.exportData(exportType, exportModel);
        } else {
            const exportMenuElement = elementRef.querySelector('.agpr-themeEngagementAnalytics-exportMenu');
            exportMenuElement.style.display = 'none';
            this.chartExportService.exportImage(exportType, elementRef, exportModel.title).then(() => {
                exportMenuElement.style.display = 'block';
            });
        }

    }

    private prepareExportData(chartType: ThemeAnalyticsChartTypeEnum): SocialChartExportModel {
        let exportModel: SocialChartExportModel;
        let key: string;
        switch (chartType) {
            case ThemeAnalyticsChartTypeEnum.ENGAGEMENT_BY_SOCIAL_MEDIA_COMPARISON:
                const totalEngagementsChartModelClone = cloneDeep(this.totalEngagementsChartModel);
                totalEngagementsChartModelClone.amchart.data.forEach(data => {
                    key = 'TOTAL_ENGAGEMENTS';
                    if (data.xValue) {
                        data['xValue' + `_${key}`] = data.xValue;
                    }
                    if (data['xValuePreviousPeriod']) {
                        data['xValuePreviousPeriod' + `_${key}`] = data['xValuePreviousPeriod'];
                    }
                });
                const engagementRateChartModelClone = cloneDeep(this.engagementRateChartModel);
                engagementRateChartModelClone.amchart.data.forEach(data => {
                    key = 'ENGAGEMENT_RATE';
                    if (data.xValue) {
                        data['xValue' + `_${key}`] = data.xValue;
                    }
                    if (data['xValuePreviousPeriod']) {
                        data['xValuePreviousPeriod' + `_${key}`] = data['xValuePreviousPeriod'];
                    }
                });

                return exportModel = {
                    title: this.localizationService.transform('Social.intelligence.themeAnalytics.engagement.engagementsBySocialNetworkComparison'),
                    exportData: this.chartExportService.setExportDataForChartGroup([totalEngagementsChartModelClone, engagementRateChartModelClone]),
                    exportLabelDefinitions: new Map<string, string>([
                        ['label',  this.localizationService.transform('Social.export.label.networkName')],
                        ['xValue_TOTAL_ENGAGEMENTS', this.localizationService.transform('Social.export.label.engagements')],
                        ['xValuePreviousPeriod_TOTAL_ENGAGEMENTS', this.localizationService.transform('Social.export.label.previousEngagements')],
                        ['xValue_ENGAGEMENT_RATE', this.localizationService.transform('Social.export.label.engagementRate')],
                        ['xValuePreviousPeriod_ENGAGEMENT_RATE', this.localizationService.transform('Social.export.label.previousEngagementRate')]
                    ])
                };
            case ThemeAnalyticsChartTypeEnum.ENGAGEMENT_TYPE_BY_SOCIAL_MEDIA_COMPARISON:
                const chartModelClone = cloneDeep(this.engagementTypeBySocialNetworkComparisonChartModel);
                chartModelClone.charts.forEach((chart, index) => {
                    if (index === 0) {
                        key = 'APPLAUSE';
                    }
                    if (index === 1) {
                        key = 'CONVERSATION';
                    }
                    if (index === 2) {
                        key = 'AMPLIFICATION';
                    }

                    chart.amchart.data.forEach(data => {
                        if (data.xValue) {
                            data['xValue' + `_${key}`] = data.xValue;
                        }
                        if (data['xValuePreviousPeriod']) {
                            data['xValuePreviousPeriod' + `_${key}`] = data['xValuePreviousPeriod'];
                        }
                    });
                });
                return exportModel = {
                    title: this.localizationService.transform('Social.intelligence.themeAnalytics.engagement.engagementTypeBySocialNetworkComparison'),
                    exportData: this.chartExportService.setExportDataForChartGroup(chartModelClone.charts),
                    exportLabelDefinitions: new Map<string, string>([
                        ['label', this.localizationService.transform('Social.export.label.networkName')],
                        ['xValue_APPLAUSE', this.localizationService.transform('Social.export.label.applause')],
                        ['xValuePreviousPeriod_APPLAUSE', this.localizationService.transform('Social.export.label.previousApplause')],
                        ['xValue_CONVERSATION', this.localizationService.transform('Social.export.label.conversation')],
                        ['xValuePreviousPeriod_CONVERSATION', this.localizationService.transform( 'Social.export.label.previousConversation')],
                        ['xValue_AMPLIFICATION', this.localizationService.transform('Social.export.label.amplification')],
                        ['xValuePreviousPeriod_AMPLIFICATION', this.localizationService.transform('Social.export.label.previousAmplification')]
                    ])
                };
            case ThemeAnalyticsChartTypeEnum.ENGAGEMENT_SUMMARY_PER_SOCIAL_MEDIA:
                return exportModel = {
                    title: this.localizationService.transform('Social.intelligence.themeAnalytics.engagement.summaryPerSocialNetwork'),
                    exportData: [this.engagementSummaryExportData],
                    exportLabelDefinitions: new Map<string, string>([
                        ['label', this.localizationService.transform('Social.export.label.networkName')],
                        ['posts', this.localizationService.transform('Social.export.label.posts')],
                        ['previousPeriodPosts', this.localizationService.transform('Social.export.label.previousPosts')],
                        ['engagements', this.localizationService.transform('Social.export.label.engagements')],
                        ['previousPeriodEngagements', this.localizationService.transform('Social.export.label.previousEngagements')],
                        ['engagementRate', this.localizationService.transform('Social.export.label.engagementRate')],
                        ['previousPeriodEngagementRate', this.localizationService.transform('Social.export.label.previousEngagementRate')],
                        ['likes', this.localizationService.transform('Social.export.label.likes')],
                        ['shares', this.localizationService.transform('Social.export.label.shares')],
                        ['replies', this.localizationService.transform('Social.export.label.replies')],
                        ['quotes', this.localizationService.transform('Social.export.label.quotes')],
                        ['views', this.localizationService.transform('Social.export.label.views')]
                    ])
                };
            default:
                console.error('cannot find chart type');
                break;
        }
    }
}
